import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hook';
import {
  type ChartType,
  setChartType,
  setHighGrainExecute,
  setSelectedTags,
} from 'store/sensorAnalysis.slice';
import { FlexBox, StyledTypography, SubText } from './SensorAnalysis.styles';
import { Box, Button, ButtonGroup, Typography } from '@mui/material';
import useAppTheme from 'hooks/useAppTheme';
import { ThemePalette } from 'mui.theme';
import { AddCircleOutline } from '@mui/icons-material';
import { SelectTagModal } from '../SensorAnalysisView/modal/TagSelectorModal';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const _palette = clientThemePalette[CLIENT_THEME];

const chartTypeBtnList = ['Individual', 'Grouped'];

export const SensorSelector = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { enableDarkTheme } = useAppTheme();
  const selectedTags = useAppSelector(
    (state) => state.sensorAnalysisReducer.selectedTags
  );

  const selectedAsset = useAppSelector(
    (state) => state.sensorAnalysisReducer.selectedAsset
  );

  const dataType = useAppSelector(
    (state) => state.sensorAnalysisReducer.dataType
  );

  const chartType = useAppSelector(
    (state) => state.sensorAnalysisReducer.chartType
  );

  const [isSelectTagModalOpen, setIsSelectTagModalOpen] = useState(false);

  const handleSelectTags = (tags: string[]) => {
    dispatch(setSelectedTags(tags));
    if (dataType === 'highGrain') {
      dispatch(setHighGrainExecute(true));
    }
  };

  const handleOpenSelectTagModal = () => {
    setIsSelectTagModalOpen(true);
  };

  const handleChartType = (newValue: ChartType) => {
    dispatch(setChartType(newValue));
  };

  useEffect(() => {
    let tagsToDisplay: string[] = [];
    let allTagsNames: string[] = [];
    const assetTags = selectedAsset?.device?.tags ?? [];
    const tagOptions = assetTags.map((tag) => tag.tagName);

    if (tagOptions) {
      const indexOfObject = tagOptions.findIndex(
        (item) => item === 'sb_ignition_status'
      );
      const defaultTag =
        indexOfObject !== -1 ? tagOptions[indexOfObject] : tagOptions[0];

      if (defaultTag) {
        tagsToDisplay = [defaultTag];
      }
      allTagsNames = tagOptions;
    }

    if (Array.isArray(selectedTags) && selectedTags.length > 0) {
      tagsToDisplay = selectedTags;
    }

    tagsToDisplay = tagsToDisplay.filter((item) => allTagsNames.includes(item));

    handleSelectTags([...tagsToDisplay]);
  }, [selectedAsset]);

  return (
    <Box
      display={'flex'}
      flexDirection={'row'}
      alignItems={'center'}
      justifyContent={'space-between'}
    >
      {isSelectTagModalOpen && (
        <SelectTagModal
          open={isSelectTagModalOpen}
          handleAbort={() => {
            setIsSelectTagModalOpen(false);
          }}
          title={t('select_tags')}
          enableDarkTheme={enableDarkTheme}
          tags={selectedAsset?.device?.tags ?? []}
          addTags={handleSelectTags}
          defaultTags={selectedTags}
          dataType={dataType}
        />
      )}
      <FlexBox sx={{ justifyContent: 'space-between' }}>
        <FlexBox
          sx={{ justifyContent: 'center', alignItems: 'center', gap: 1 }}
        >
          <StyledTypography>
            <SubText
              sx={{
                color: enableDarkTheme
                  ? ThemePalette.typography.grey
                  : ThemePalette.typography.black,
              }}
            >
              {t('readings_for_2')}
            </SubText>
          </StyledTypography>
          <Button
            size="small"
            id="basic-button"
            sx={{
              border: `1px solid ${
                enableDarkTheme
                  ? ThemePalette.typography.white
                  : ThemePalette.typography.lightGrey
              }`,
              color: (enableDarkTheme ? _palette.dark : _palette.light)
                .textPrimary,
              borderRadius: '5px',
              paddingLeft: '15px',
              paddingRight: '15px',
              textTransform: 'none',
            }}
            onClick={handleOpenSelectTagModal}
          >
            <AddCircleOutline
              sx={{
                paddingRight: '8px',
                color: (enableDarkTheme ? _palette.dark : _palette.light)
                  .textPrimary,
                fontSize: '16px',
              }}
            />
            {t('select_more_tags')}
          </Button>
        </FlexBox>
      </FlexBox>

      <Box display={'flex'} flexDirection={'row'} gap={1}>
        <Typography
          sx={{
            fontSize: '12px',
            color: enableDarkTheme ? '#8A8A8A' : ThemePalette.typography.black,
            paddingTop: '8px',
            paddingRight: '10px',
          }}
        >
          {t('view_graph_as')}
        </Typography>
        <ButtonGroup
          size="small"
          aria-label={t('small_button_group')}
          disabled={dataType === 'highGrain'}
        >
          {chartTypeBtnList.map((item, index) => {
            return (
              <Button
                key={index}
                sx={{
                  backgroundColor:
                    chartType === item
                      ? enableDarkTheme
                        ? _palette.dark.primary
                        : _palette.light.primary
                      : enableDarkTheme
                      ? _palette.dark.boxBackground
                      : (enableDarkTheme ? _palette.dark : _palette.light)
                          .boxBackground,
                  color:
                    chartType === item
                      ? '#ffffff'
                      : (enableDarkTheme ? _palette.dark : _palette.light)
                          .textPrimary,
                  textTransform: 'none',
                  borderColor: enableDarkTheme
                    ? _palette.dark.borderGrey
                    : _palette.light.borderGrey,
                  '&:hover': {
                    backgroundColor: enableDarkTheme
                      ? _palette.dark.primaryDark
                      : _palette.light.primaryDark,
                    color: '#ffffff',
                  },
                  '&.Mui-disabled': {
                    color: enableDarkTheme
                      ? 'rgba(255,255,255,0.3)'
                      : 'rgba(0,0,0,0.3)',
                    backgroundColor: enableDarkTheme
                      ? _palette.dark.boxBackground
                      : (enableDarkTheme ? _palette.dark : _palette.light)
                          .boxBackground,
                  },
                }}
                onClick={() => {
                  handleChartType(item as ChartType);
                }}
                disabled={dataType !== 'regular' && item === 'Grouped'}
              >
                {t(
                  item === 'Individual'
                    ? 'chart_type_individual'
                    : 'chart_type_grouped'
                )}
              </Button>
            );
          })}
        </ButtonGroup>
      </Box>
    </Box>
  );
};

export default SensorSelector;
